import {
  PrimitiveValueType,
  GenericStringObject,
  DynamicFieldRendererProps,
  ISelectableOption,
} from "../../../../types";
import {
  getInputType,
  getIsDisabled,
  getDynamicDependentFieldsAndValues,
  getSelectedOptions,
  getTypeAheadRequest,
  getRowDataFieldValue,
  getSelectableOptions,
  getResolvedDynamicRowDataValue,
  getTranslatedTooltip,
  getSelectedOption,
  convertToOptions,
} from "../../helpers";
import RenderAutoCompleteMenu from "./RenderAutoCompleteMenu";
import RenderCheckbox from "./RenderCheckbox";
import RenderCoRelation from "./RenderCoRelation";
import RenderInput from "./RenderInput";
import RenderMultiAutoCompleteMenu from "./RenderMultiAutoCompleteMenu";
import { extractErrorAndMessage } from "../../validations";
import RenderMultiInput from "./RenderMultiInput";
import { useUserContext } from "../../../../contexts/UserContext";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import {
  CUSTOM_MULTI_INPUT_CHIPS_COMPONENT_KEYS,
  CUSTOM_MULTI_INPUT_CHIPS_REQUIRED_KEYS,
  CUSTOM_CHECKBOX_LIST_COMPONENT_KEYS,
  CUSTOM_MULTI_INPUT_COMPONENT_KEYS,
  CUSTOM_MULTI_INPUT_REQUIRED_KEYS,
  CUSTOM_MULTI_SELECT_REQUIRED_KEYS,
  CUSTOM_MULTIVALUED_KEYS_NEEDED_CUSTOM_OR_ENUM_DROPDOWN_OPTIONS,
  IMPLEMENTED_SETUP_KEYS,
  CUSTOM_PLAIN_TEXT_COMPONENT_KEYS,
  CURRENCY_FIELD_KEYS,
} from "../../constants";
import { useTranslation } from "react-i18next";
import RenderMultiInputChips from "./RenderMultiInputChips";
import RenderActionButton from "./RenderActionButton";
import ETCAbsenseCodeClassificationReference from "../custom-renderers/etc-absense-code/ETCAbsenseCodeClassificationReference";
import CopyDiscountSettings from "../custom-renderers/discounts/CopyDiscountSettings";
import RenderInventoryBuildingsRoutesCheckboxList from "../custom-renderers/inventory-buildings/RenderInventoryBuildingsRoutesCheckboxList";
import RenderInventoryBuildingZonesPlainTextList from "../custom-renderers/inventory-buildings/RenderInventoryBuildingZonesPlainTextList";
import RenderCurrencyInput from "./RenderCurrencyInput";
import RenderObjects from "./RenderObjects";
import {
  DatePickerWithTooltip,
  DropdownWithTooltip,
  MegaDateTimePickerWithTooltip,
} from "../../../common/Tooltip";

export default function DynamicFieldRenderer({
  propertyKey,
  property,
  rowDataReducerState,
  dispatch,
  isDialogField,
}: Readonly<DynamicFieldRendererProps>): JSX.Element {
  const { t, i18n } = useTranslation();
  const { selectedTabSectionKey } = useUIStateContext();
  const {
    iamUserState,
    setupBaseRequestInfo,
    selected,
    evaluatedResults,
    setupListNavInfoByDepartment,
  } = useUserContext();
  const { schema, rowData, isReadOnly, dialogRowData, rowDataList } =
    rowDataReducerState;

  const {
    title,
    description,
    type: propertyType,
    format: dateFormat,
    isStore,
    isGroupId,
    companyKey,
    storeKey,
    dependentFields,
    typeaheadConfig,
    default: defaultValue,
    evalMethod,
    enum: enumValues,
    enumLabels,
    key: propertySubKey,
    items,
    triggerDependencyRule,
    requiresClear,
    maximum,
    minimum,
    maxLength,
    minLength,
  } = property;

  const translateWithFallback = (key: string, fallback: string) => {
    const namespaces = [
      selectedTabSectionKey,
      ...IMPLEMENTED_SETUP_KEYS,
    ].filter(Boolean) as string[];
    return t(`column.${key}`, { defaultValue: fallback, ns: namespaces });
  };

  const translatedTitle = i18n.language?.startsWith("en")
    ? title
    : translateWithFallback(propertyKey, title);

  const translatedDescription = getTranslatedTooltip(
    propertyKey,
    description ?? "",
    t,
    i18n.language,
    rowDataReducerState?.schema?.title,
  );
  const inputValue = getRowDataFieldValue({
    propertyKey,
    rowData,
    isDialogField,
    dialogRowData,
    selectedTabSectionKey,
  });

  const type = getInputType(propertyType);
  const isRequired = defaultValue !== null;
  const isDisabled = getIsDisabled({ property, rowData, isReadOnly });
  const dependencyRules = schema?.dependencies?.[propertyKey];
  const { error, message } = extractErrorAndMessage(
    propertyKey,
    undefined,
    undefined,
    rowDataReducerState,
    isDialogField,
  );

  const existingStoreIdKey = typeaheadConfig?.storeId;
  const existingStoreIdValue = existingStoreIdKey
    ? (getResolvedDynamicRowDataValue({
        propertyKey,
        dynamicField: existingStoreIdKey,
        rowData,
        selectedTabSectionKey,
      }) as string)
    : undefined;

  const existingGroupIdKey = typeaheadConfig?.groupId;
  const existingGroupIdValue = existingGroupIdKey
    ? (getResolvedDynamicRowDataValue({
        propertyKey,
        dynamicField: existingGroupIdKey,
        rowData,
        selectedTabSectionKey,
      }) as string)
    : undefined;

  const typeAheadRequest =
    typeaheadConfig &&
    getTypeAheadRequest({
      propertyKey,
      typeaheadConfig,
      rowIndex: undefined,
      rowDataReducerState,
      setupBaseRequestInfo,
      existingStoreIdValue,
      existingGroupIdValue,
      iamUserState,
      setupListNavInfoByDepartment,
    });

  const dynamicDependentFieldsAndValues = getDynamicDependentFieldsAndValues(
    typeaheadConfig,
    propertyKey,
    rowDataReducerState,
  );

  if (type === "boolean") {
    return (
      <RenderCheckbox
        title={translatedTitle}
        description={translatedDescription}
        propertyKey={propertyKey}
        propertySubKey={undefined}
        rowIndex={undefined}
        inputValue={inputValue}
        dispatch={dispatch}
        isDialogField={isDialogField}
        isDisabled={isDisabled}
        dependencyRules={dependencyRules}
        triggerDependencyRule={triggerDependencyRule}
        evalMethod={evalMethod}
        selectedTabSectionKey={selectedTabSectionKey}
        rowDataReducerState={rowDataReducerState}
      />
    );
  }

  const renderDropdownOrAutoComplete = () => {
    if (enumValues || isStore || isGroupId) {
      const optionsList = getSelectableOptions({
        propertyKey,
        enumValues: enumValues as PrimitiveValueType[],
        enumLabels,
        iamUserState,
        selected,
        isStore,
        isGroupId,
        selectedTabSectionKey,
        rowData,
        rowDataList,
        evaluatedResults,
        setupListNavInfoByDepartment,
      });
      const selectedOption = getSelectedOption({
        inputValue,
        optionsList,
      });

      return (
        <DropdownWithTooltip
          title={translatedTitle}
          description={translatedDescription}
          tooltip={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={undefined}
          propertySubKey={undefined}
          selectedOption={selectedOption}
          optionsList={optionsList}
          companyKey={companyKey}
          storeKey={storeKey}
          dependentFields={dependentFields}
          evalMethod={evalMethod}
          isRequired={isRequired}
          isDisabled={isDisabled}
          error={error}
          message={message}
          dependencyRules={dependencyRules}
          triggerDependencyRule={triggerDependencyRule}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
          isGroupId={isGroupId}
        />
      );
    }

    if (typeAheadRequest) {
      return (
        <RenderAutoCompleteMenu
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={undefined}
          propertySubKey={undefined}
          inputValue={inputValue as PrimitiveValueType}
          companyKey={companyKey}
          typeAheadRequest={typeAheadRequest}
          evalMethod={evalMethod}
          evalTypeAheadMethods={typeaheadConfig.evalMethods}
          dependentFields={dependentFields}
          dynamicDependentFieldsAndValues={dynamicDependentFieldsAndValues}
          existingStoreIdKey={typeaheadConfig.storeId}
          existingStoreIdValue={existingStoreIdValue}
          existingGroupIdKey={existingGroupIdKey}
          existingGroupIdValue={existingGroupIdValue}
          isRequired={isRequired}
          isDisabled={isDisabled}
          error={error}
          message={message}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    return (
      <RenderInput
        title={translatedTitle}
        description={translatedDescription}
        propertyKey={propertyKey}
        rowIndex={undefined}
        propertySubKey={undefined}
        inputValue={inputValue as PrimitiveValueType}
        type={type}
        isRequired={isRequired}
        isDisabled={isDisabled}
        storeKey={storeKey}
        evalMethod={evalMethod}
        error={error}
        message={message}
        isDialogField={isDialogField}
        selectedTabSectionKey={selectedTabSectionKey}
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
        maxLength={maxLength}
        minLength={minLength}
        maximum={maximum}
        minimum={minimum}
      />
    );
  };

  const renderStringOrNumber = () => {
    if (CURRENCY_FIELD_KEYS.includes(propertyKey)) {
      return (
        <RenderCurrencyInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={undefined}
          propertySubKey={undefined}
          inputValue={inputValue as PrimitiveValueType}
          type={type}
          isRequired={isRequired}
          isDisabled={isDisabled}
          storeKey={storeKey}
          evalMethod={evalMethod}
          error={error}
          message={message}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    if (dateFormat === "date") {
      return (
        <DatePickerWithTooltip
          title={translatedTitle}
          description={translatedDescription}
          tooltip={translatedDescription}
          propertyKey={propertyKey}
          inputValue={inputValue as string}
          isRequired={isRequired}
          error={error}
          message={message}
          isDialogField={isDialogField}
          dispatch={dispatch}
          isDisabled={isDisabled}
          evalMethod={evalMethod}
          selectedTabSectionKey={selectedTabSectionKey}
        />
      );
    }

    if (dateFormat === "dateTime") {
      return (
        <MegaDateTimePickerWithTooltip
          title={translatedTitle}
          description={translatedDescription}
          tooltip={translatedDescription}
          propertyKey={propertyKey}
          inputValue={inputValue as string}
          isRequired={isRequired}
          error={error}
          message={message}
          isDialogField={isDialogField}
          dispatch={dispatch}
          isDisabled={isDisabled}
          evalMethod={evalMethod}
          selectedTabSectionKey={selectedTabSectionKey}
        />
      );
    }

    return renderDropdownOrAutoComplete();
  };

  const renderArray = () => {
    if (items?.type === "object") {
      return (
        <RenderCoRelation
          propertyKey={propertyKey}
          property={property}
          inputValue={inputValue as GenericStringObject[]}
          evalMethod={evalMethod}
          dependencyRules={dependencyRules}
          triggerDependencyRule={triggerDependencyRule}
          setupBaseRequestInfo={setupBaseRequestInfo}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    const selectedOptions = getSelectedOptions(
      inputValue,
      undefined,
      enumLabels,
    );

    if (CUSTOM_PLAIN_TEXT_COMPONENT_KEYS.includes(propertyKey)) {
      return (
        <RenderInventoryBuildingZonesPlainTextList
          title={translatedTitle}
          propertyKey={propertyKey}
          inputValues={selectedOptions}
          rowDataReducerState={rowDataReducerState}
        />
      );
    }

    if (CUSTOM_CHECKBOX_LIST_COMPONENT_KEYS.includes(propertyKey)) {
      const optionsList = getSelectableOptions({
        propertyKey,
        enumValues: enumValues as PrimitiveValueType[],
        enumLabels,
        iamUserState,
        selected,
        isStore,
        isGroupId,
        selectedTabSectionKey,
        rowData,
        evaluatedResults,
        setupListNavInfoByDepartment,
      });
      return (
        <RenderInventoryBuildingsRoutesCheckboxList
          title={translatedTitle}
          propertyKey={propertyKey}
          inputValues={selectedOptions}
          optionsList={optionsList}
          isDisabled={isDisabled}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    if (CUSTOM_MULTI_INPUT_COMPONENT_KEYS.includes(propertyKey)) {
      return (
        <RenderMultiInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          propertySubKey={propertySubKey ?? propertyKey}
          inputValues={selectedOptions}
          isRequired={CUSTOM_MULTI_INPUT_REQUIRED_KEYS.includes(propertyKey)}
          isDisabled={isDisabled}
          error={error}
          message={message}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    if (CUSTOM_MULTI_INPUT_CHIPS_COMPONENT_KEYS.includes(propertyKey)) {
      return (
        <RenderMultiInputChips
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          propertySubKey={propertySubKey ?? propertyKey}
          inputValues={selectedOptions}
          isRequired={CUSTOM_MULTI_INPUT_CHIPS_REQUIRED_KEYS.includes(
            propertyKey,
          )}
          isDisabled={isDisabled}
          error={error}
          message={message}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
    }

    const shouldOverrideOptions =
      CUSTOM_MULTIVALUED_KEYS_NEEDED_CUSTOM_OR_ENUM_DROPDOWN_OPTIONS.includes(
        propertyKey,
      );

    const enumOptions: PrimitiveValueType[] = items?.enum ?? enumValues ?? [];

    const overrideTypeAheadOptionsList: ISelectableOption[] | undefined =
      shouldOverrideOptions
        ? getSelectableOptions({
            propertyKey,
            enumValues: enumOptions,
            enumLabels,
            iamUserState,
            selected,
            isStore,
            isGroupId,
            selectedTabSectionKey,
            rowData,
            evaluatedResults,
            setupListNavInfoByDepartment,
          })
        : undefined;

    return (
      <RenderMultiAutoCompleteMenu
        title={translatedTitle}
        description={translatedDescription}
        propertyKey={propertyKey}
        propertySubKey={propertySubKey ?? propertyKey}
        inputValues={selectedOptions}
        optionsList={overrideTypeAheadOptionsList}
        typeAheadRequest={typeAheadRequest}
        evalTypeAheadMethods={typeaheadConfig?.evalMethods}
        dynamicDependentFieldsAndValues={dynamicDependentFieldsAndValues}
        isRequired={CUSTOM_MULTI_SELECT_REQUIRED_KEYS.includes(propertyKey)}
        isDisabled={isDisabled}
        error={error}
        message={message}
        selectedTabSectionKey={selectedTabSectionKey}
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
      />
    );
  };

  const renderButton = () => {
    return (
      <RenderActionButton
        title={translatedTitle}
        propertyKey={propertyKey}
        isDisabled={isDisabled}
        requiresClear={requiresClear}
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
      />
    );
  };

  const renderETCAbsenseCodeClassificationReference = () => {
    return (
      <ETCAbsenseCodeClassificationReference
        inputValues={convertToOptions(
          enumValues as PrimitiveValueType[],
          enumLabels,
        )}
      />
    );
  };

  const renderCopyDiscountSettings = () => {
    return (
      <CopyDiscountSettings
        rowDataReducerState={rowDataReducerState}
        dispatch={dispatch}
      />
    );
  };

  switch (type) {
    case "string":
    case "number":
    case "integer":
      return renderStringOrNumber();
    case "array":
      return renderArray();
    case "object":
      return (
        <RenderObjects
          propertyKey={propertyKey}
          property={property}
          dependencyRules={dependencyRules}
          triggerDependencyRule={triggerDependencyRule}
          setupBaseRequestInfo={setupBaseRequestInfo}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );

    case "button":
      return renderButton();
    case "etc-absense-code-classification-ref-component":
      return renderETCAbsenseCodeClassificationReference();
    case "discount-copy-settings-component":
      return renderCopyDiscountSettings();

    default:
      return (
        <RenderInput
          title={translatedTitle}
          description={translatedDescription}
          propertyKey={propertyKey}
          rowIndex={undefined}
          propertySubKey={undefined}
          inputValue={inputValue as PrimitiveValueType}
          type={type}
          isRequired={isRequired}
          isDisabled={isDisabled}
          maxLength={maxLength}
          minLength={minLength}
          maximum={maximum}
          minimum={minimum}
          storeKey={storeKey}
          evalMethod={evalMethod}
          error={error}
          message={message}
          isDialogField={isDialogField}
          selectedTabSectionKey={selectedTabSectionKey}
          rowDataReducerState={rowDataReducerState}
          dispatch={dispatch}
        />
      );
  }
}
